<template>
  <div class="forgotPassword">
    <Loading v-if="loading" />
    <div class="forgotPassword__image">
      <div class="container">
        <img
          class="image"
          src="@/assets/icons/Apoterkset.svg"
          alt="Apoterkset"
        />
      </div>
    </div>
    <div class="forgotPassword__content">
      <div class="container">
        <div class="forgotPassword__content-head">
          <div class="forgotPassword__content-head-lang">
            <LangChange />
          </div>
          <div class="forgotPassword__content-head-backTo">
            <Link :title="$t('backToLogIn')" @backTo="backTo" />
          </div>
        </div>
        <div class="forgotPassword__content-form">
          <div class="form">
            <div class="form-title">
              <p>{{ $t('forgotPassword') }}</p>
            </div>
            <div class="form-text">
              <p>{{ $t('forgotPasswordTex') }}</p>
            </div>
            <div
              :class="{
                'form-input': !status.error,
                'form-inputError': status.error || sentEmail === true
              }"
            >
              <Input
                v-model="email"
                type="email"
                name="email"
                :title="$t('email')"
                :value="email"
                validation="email"
                :validationMessages="{
                  required: $t('validation.required'),
                  email: $t('validation.email')
                }"
                forName="email"
                @change="handleChange"
              />
            </div>
            <div class="form-action">
              <div class="form-error" v-if="status.error">
                <div class="error" role="alert">
                  {{ $t(status.message || 'error') }}
                </div>
              </div>
              <div
                class="form-sentEmail"
                v-if="sentEmail && status.error !== true"
              >
                <p>{{ $t('sentEmail') }}</p>
              </div>
            </div>
            <Button
              id="login"
              variant="logIn"
              type="button"
              @click="forgotPassword"
              :title="$t('sendResetLink')"
              data-testid="sendResetLinkButton"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import LangChange from '@/views/Language.vue';
import Loading from '@/views/Loading.vue';

import router from '@/router';
import { InputFileEvent } from '@/types';

@Options({
  name: 'ForgotPassword',
  components: {
    Loading,
    LangChange
  },
  data() {
    return {
      email: null,
      loading: false,
      sentEmail: false
    };
  },
  computed: {
    ...mapState('account', ['status'])
  },
  methods: {
    ...mapActions('account', ['resetPassword']),
    async forgotPassword() {
      this.loading = true;

      if (this.email) {
        await this.resetPassword({ email: this.email });

        this.email = '';
        this.sentEmail = true;
      }

      this.$i18n.locale = localStorage.getItem('locale') || 'en';
      this.loading = false;
    },
    handleChange(e: InputFileEvent) {
      this[e.target.name] = e.target.value;
    },
    backTo() {
      router.push({ name: 'Login' });
      this.status.error = '';
    }
  }
})
export default class ForgotPassword extends Vue {}
</script>

<style lang="scss" scoped>
.forgotPassword {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100vw;
  height: 100vh;
  .forgotPassword__image {
    width: 50%;
    height: 100vh;
    .container {
      width: 100%;
      height: 100%;
      background-color: $light-grey;
      .image {
        height: 100%;
      }
    }
  }
  .forgotPassword__content {
    width: 50%;
    height: 100vh;
    .container {
      width: 100%;
      height: 100%;
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: $light-grey;
      .forgotPassword__content-head {
        .forgotPassword__content-head-lang {
          position: absolute;
          width: 172px;
          height: 48px;
          right: 32px;
          top: 32px;
          border-radius: 8px;
          background-color: $light-grey;
        }
        .forgotPassword__content-head-backTo {
          position: absolute;
          width: 172px;
          height: 50px;
          left: 32px;
          top: 32px;
          border-radius: 8px;
          background-color: $ligher-grey;
        }
      }
      .forgotPassword__content-form {
        max-width: 416px;
        border-radius: 8px;
        .form {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 32px;
          border-radius: 8px;
          background: $white;
          .form-title {
            width: 352px;
            margin-bottom: 16px;
            p {
              font-size: $twentyFourSize;
              line-height: $twentyEightSize;
              font-family: 'Ubuntu-Medium';
            }
          }
          .form-text {
            width: 352px;
            margin-bottom: 30px;
            p {
              font-size: $fourteenSize;
              line-height: $twentyOneSize;
              font-weight: 400;
              color: $label;
            }
          }
          .form-input {
            width: 352px;
            margin-bottom: 32px;
          }
          .form-inputError {
            width: 352px;
            margin-bottom: 15px;
            .input {
              margin-bottom: 0;
            }
          }
          .form-action {
            display: flex;
            justify-content: space-between;
            width: 352px;
            .form-error {
              margin-top: 16px;
              margin-bottom: 15px;
              .error {
                background-color: $white;
                border: none;
                border-radius: 0;
                padding: 0;
                margin-bottom: 0;
                color: $messageError;
                font-size: $twelveSize;
                text-align: center;
              }
            }
            .form-sentEmail {
              margin-top: 16px;
              margin-bottom: 15px;
              p {
                font-size: $fourteenSize;
                line-height: $twentyOneSize;
                font-weight: 400;
                color: $main-green;
              }
            }
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
.form-inputs {
  .formkit-outer {
    .formkit-wrapper {
      .formkit-inner {
        .formkit-input {
          &:hover {
            border: 1px solid $main-green;
          }
        }
      }
    }
  }
}
</style>
